/**
 * Comment Composer Component
 * Task 5.3: Implement Comment Composer
 *
 * Uses PDS design tokens for consistent Pantheon branding.
 *
 * Features:
 * - Text input with markdown support
 * - @mention autocomplete with team member list
 * - Post and Cancel buttons
 * - Loading state during submission
 * - Error handling with user feedback
 */

import { useState, useRef, useEffect } from 'react';
import type { ElementFingerprint } from '../../lib/types';
import { tokens } from '../../styles/tokens';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  avatar?: string | null;
}

export interface CommentComposerProps {
  elementFingerprint?: ElementFingerprint;
  teamMembers: TeamMember[];
  onSubmit: (data: { content: string }) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  position?: { top: number; left: number };
}

export function CommentComposer({
  elementFingerprint: _elementFingerprint,
  teamMembers,
  onSubmit,
  onCancel,
  isSubmitting = false,
  error = null,
  position: _position,
}: CommentComposerProps) {
  const [content, setContent] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  // Filter team members based on mention query
  const filteredMembers = mentionQuery
    ? teamMembers.filter(
        (member) =>
          member.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
          member.email.toLowerCase().includes(mentionQuery.toLowerCase())
      )
    : teamMembers;

  // Handle textarea input for @mentions
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContent(value);

    // Check for @mention trigger
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentionDropdown(true);
      setSelectedMentionIndex(0);

      // Calculate position for mention dropdown
      const textarea = textareaRef.current;
      if (textarea) {
        const coords = getCaretCoordinates(textarea, cursorPosition);
        setMentionPosition({
          top: coords.top + 20, // 20px offset for dropdown positioning
          left: coords.left,
        });
      }
    } else {
      setShowMentionDropdown(false);
      setMentionQuery('');
    }
  };

  // Handle mention selection
  const selectMention = (member: TeamMember) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);

    // Replace @query with @name
    const mentionMatch = textBeforeCursor.match(/@\w*$/);
    if (mentionMatch) {
      const newText =
        textBeforeCursor.replace(/@\w*$/, `@${member.name} `) + textAfterCursor;
      setContent(newText);
      setShowMentionDropdown(false);
      setMentionQuery('');

      // Restore focus
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = textBeforeCursor.replace(/@\w*$/, `@${member.name} `).length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionDropdown || filteredMembers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex((prev) =>
        prev < filteredMembers.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter' && filteredMembers.length > 0) {
      e.preventDefault();
      selectMention(filteredMembers[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
      setMentionQuery('');
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) return;

    await onSubmit({
      content: content.trim(),
    });
  };

  // Close mention dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (composerRef.current && !composerRef.current.contains(event.target as Node)) {
        setShowMentionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <div
      ref={composerRef}
      className="comment-composer"
      data-testid="comment-composer"
      data-multidev-sdk="true"
      style={{
        // Position is handled by Floating UI wrapper - don't override it
        zIndex: 1000001,
        backgroundColor: tokens.colors.bgDefault,
        borderRadius: tokens.borders.radiusContainer,
        boxShadow: tokens.elevation.overlay,
        padding: tokens.spacing.m,
        width: '360px',
        maxWidth: 'calc(100vw - 32px)',
        fontFamily: tokens.typography.fontFamily,
        pointerEvents: 'auto',
      }}
    >
      <form onSubmit={handleSubmit}>
        {/* Comment Text */}
        <div style={{ marginBottom: tokens.spacing.s }}>
          <label
            htmlFor="comment-content"
            style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.textDefault,
              marginBottom: tokens.spacing['5xs'],
            }}
          >
            Comment
          </label>
          <div style={{ position: 'relative' }}>
            <textarea
              ref={textareaRef}
              id="comment-content"
              value={content}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              data-testid="comment-textarea"
              placeholder="Add your comment... (use @name to mention team members)"
              rows={4}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: `${tokens.spacing['2xs']} ${tokens.spacing.s}`,
                fontSize: '14px',
                color: tokens.colors.textDefault,
                backgroundColor: tokens.colors.bgDefault,
                border: `1px solid ${tokens.colors.borderInput}`,
                borderRadius: tokens.borders.radiusInput,
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />

            {/* Mention Autocomplete Dropdown */}
            {showMentionDropdown && filteredMembers.length > 0 && mentionPosition && (
              <div
                className="mention-dropdown"
                data-testid="mention-dropdown"
                style={{
                  position: 'absolute',
                  top: `${mentionPosition.top}px`,
                  left: `${mentionPosition.left}px`,
                }}
              >
                {filteredMembers.map((member, index) => (
                  <button
                    key={member.id}
                    type="button"
                    className={`mention-item ${
                      index === selectedMentionIndex ? 'selected' : ''
                    }`}
                    data-testid={`mention-item-${member.id}`}
                    onClick={() => selectMention(member)}
                    onMouseEnter={() => setSelectedMentionIndex(index)}
                  >
                    {member.avatar && (
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className="mention-avatar"
                      />
                    )}
                    <div className="mention-info">
                      <div className="mention-name">{member.name}</div>
                      <div className="mention-email">{member.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ fontSize: '11px', color: tokens.colors.textSecondary, marginTop: tokens.spacing['5xs'] }}>
            Markdown supported. Use @name to mention team members.
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            data-testid="composer-error"
            role="alert"
            style={{
              padding: `${tokens.spacing['2xs']} ${tokens.spacing.s}`,
              backgroundColor: tokens.colors.criticalBg,
              color: tokens.colors.criticalFg,
              borderRadius: tokens.borders.radiusContainer,
              fontSize: '13px',
              marginBottom: tokens.spacing.s,
            }}
          >
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: tokens.spacing['2xs'], justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            data-testid="cancel-button"
            disabled={isSubmitting}
            style={{
              padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
              fontSize: '14px',
              fontWeight: tokens.typography.fontWeight.medium,
              color: tokens.colors.textDefault,
              backgroundColor: tokens.colors.bgDefault,
              border: `1px solid ${tokens.colors.borderInput}`,
              borderRadius: tokens.borders.radiusButton,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="post-button"
            disabled={isSubmitting || !content.trim()}
            style={{
              padding: `${tokens.spacing['2xs']} ${tokens.spacing.m}`,
              fontSize: '14px',
              fontWeight: tokens.typography.fontWeight.medium,
              color: tokens.colors.textReverse,
              backgroundColor: (isSubmitting || !content.trim()) ? tokens.colors.primaryLight : tokens.colors.buttonPrimaryBg,
              border: 'none',
              borderRadius: tokens.borders.radiusButton,
              cursor: (isSubmitting || !content.trim()) ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Get caret coordinates in textarea for mention dropdown positioning
 * Based on: https://github.com/component/textarea-caret-position
 */
function getCaretCoordinates(
  element: HTMLTextAreaElement,
  position: number
): { top: number; left: number } {
  const div = document.createElement('div');
  const style = getComputedStyle(element);

  // Copy styles
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.font = style.font;
  div.style.padding = style.padding;
  div.style.border = style.border;
  div.style.width = style.width;

  document.body.appendChild(div);

  const text = element.value.substring(0, position);
  div.textContent = text;

  const span = document.createElement('span');
  span.textContent = element.value.substring(position) || '.';
  div.appendChild(span);

  const rect = element.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();

  document.body.removeChild(div);

  return {
    top: spanRect.top - rect.top,
    left: spanRect.left - rect.left,
  };
}
